/**
 * The FlowScript tokenizer.
 *
 * Single forward pass, no backtracking, no regular expressions driving the
 * scan. It never throws: malformed input produces a diagnostic and the scanner
 * resynchronises, because the editor calls this on every keystroke and half of
 * those keystrokes land on syntactically incomplete text.
 */
import type { SourcePosition, SourceRange } from '../ast/source';
import type { Diagnostic } from '../diagnostics/types';
import { DiagnosticCode } from '../diagnostics/types';
import type { Token, TokenType } from './tokens';
import { makeEofToken } from './tokens';

export interface TokenizeResult {
  /** All tokens, comments included, terminated by a single `eof` token. */
  readonly tokens: readonly Token[];
  readonly diagnostics: readonly Diagnostic[];
  readonly source: string;
}

export interface TokenizeLimits {
  /** Refuse documents larger than this, in characters. */
  readonly maxSourceLength: number;
  /** Refuse documents with more tokens than this. */
  readonly maxTokens: number;
}

export const DEFAULT_TOKENIZE_LIMITS: TokenizeLimits = {
  maxSourceLength: 2_000_000,
  maxTokens: 400_000,
};

/** Characters that end a `word` run. Everything else is label material. */
const DELIMITERS = new Set(['{', '}', '(', ')', ';', ':', ',', '@', '"', '\\']);

function isWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\r' || ch === '\n' || ch === '\f' || ch === '\v';
}

function isDelimiter(ch: string): boolean {
  return DELIMITERS.has(ch);
}

const SINGLE_CHAR_TOKENS: Readonly<Record<string, TokenType>> = {
  '{': 'lbrace',
  '}': 'rbrace',
  '(': 'lparen',
  ')': 'rparen',
  ';': 'semicolon',
  ':': 'colon',
  ',': 'comma',
  '@': 'at',
  '\\': 'escape',
};

class Scanner {
  private offset = 0;
  private line = 1;
  private column = 1;
  private sawNewline = false;

  constructor(private readonly source: string) {}

  get atEnd(): boolean {
    return this.offset >= this.source.length;
  }

  get pos(): SourcePosition {
    return { offset: this.offset, line: this.line, column: this.column };
  }

  /** Consumes and clears the "a newline appeared since the last token" flag. */
  takeNewlineFlag(): boolean {
    const value = this.sawNewline;
    this.sawNewline = false;
    return value;
  }

  peek(ahead = 0): string {
    return this.source[this.offset + ahead] ?? '';
  }

  advance(): string {
    const ch = this.source[this.offset] ?? '';
    this.offset += 1;
    if (ch === '\n') {
      this.line += 1;
      this.column = 1;
      this.sawNewline = true;
    } else {
      // Column counts UTF-16 code units, matching CodeMirror's own model.
      this.column += 1;
    }
    return ch;
  }

  skipWhitespace(): void {
    while (!this.atEnd && isWhitespace(this.peek())) {
      this.advance();
    }
  }

  slice(from: number, to: number): string {
    return this.source.slice(from, to);
  }
}

export function tokenize(
  source: string,
  limits: TokenizeLimits = DEFAULT_TOKENIZE_LIMITS,
): TokenizeResult {
  const diagnostics: Diagnostic[] = [];

  if (source.length > limits.maxSourceLength) {
    const zero: SourcePosition = { offset: 0, line: 1, column: 1 };
    return {
      tokens: [makeEofToken({ start: zero, end: zero }, 0)],
      diagnostics: [
        {
          code: DiagnosticCode.DocumentTooLarge,
          severity: 'error',
          message: `Document is too large to analyse (${source.length} characters; the limit is ${limits.maxSourceLength}).`,
          range: { start: zero, end: zero },
          suggestion: 'Split the flow into several projects.',
          params: { size: source.length, limit: limits.maxSourceLength },
        },
      ],
      source,
    };
  }

  const scanner = new Scanner(source);
  const tokens: Token[] = [];

  const push = (
    type: TokenType,
    start: SourcePosition,
    value: string,
    normalized: string,
  ): void => {
    tokens.push({
      type,
      value,
      normalized,
      range: { start, end: scanner.pos },
      precededByNewline: scanner.takeNewlineFlag(),
      index: tokens.length,
    });
  };

  while (!scanner.atEnd) {
    scanner.skipWhitespace();
    if (scanner.atEnd) break;

    if (tokens.length >= limits.maxTokens) {
      const here = scanner.pos;
      diagnostics.push({
        code: DiagnosticCode.DocumentTooLarge,
        severity: 'error',
        message: `Document has too many tokens to analyse (the limit is ${limits.maxTokens}).`,
        range: { start: here, end: here },
        params: { limit: limits.maxTokens },
      });
      break;
    }

    const start = scanner.pos;
    const ch = scanner.peek();

    // --- comments -------------------------------------------------------
    if (ch === '/' && scanner.peek(1) === '/') {
      while (!scanner.atEnd && scanner.peek() !== '\n') scanner.advance();
      push('line-comment', start, scanner.slice(start.offset, scanner.pos.offset), '');
      continue;
    }

    if (ch === '/' && scanner.peek(1) === '*') {
      scanner.advance();
      scanner.advance();
      let closed = false;
      while (!scanner.atEnd) {
        if (scanner.peek() === '*' && scanner.peek(1) === '/') {
          scanner.advance();
          scanner.advance();
          closed = true;
          break;
        }
        scanner.advance();
      }
      const range: SourceRange = { start, end: scanner.pos };
      if (!closed) {
        diagnostics.push({
          code: DiagnosticCode.UnterminatedBlockComment,
          severity: 'error',
          message: 'Block comment is never closed.',
          range,
          suggestion: 'Add `*/` to close the comment.',
        });
      }
      push('block-comment', start, scanner.slice(start.offset, scanner.pos.offset), '');
      continue;
    }

    // --- strings --------------------------------------------------------
    if (ch === '"') {
      scanner.advance(); // opening quote
      let contents = '';
      let closed = false;
      while (!scanner.atEnd) {
        const c = scanner.peek();
        if (c === '\n') break; // strings do not span lines — keeps recovery local
        if (c === '\\') {
          scanner.advance();
          const escaped = scanner.advance();
          contents += unescapeChar(escaped);
          continue;
        }
        scanner.advance();
        if (c === '"') {
          closed = true;
          break;
        }
        contents += c;
      }
      const range: SourceRange = { start, end: scanner.pos };
      if (!closed) {
        diagnostics.push({
          code: DiagnosticCode.UnterminatedString,
          severity: 'error',
          message: 'String is never closed.',
          range,
          suggestion: 'Add a closing `"` on the same line.',
        });
      }
      push('string', start, scanner.slice(start.offset, scanner.pos.offset), contents);
      continue;
    }

    // --- single-character structure ------------------------------------
    const single = SINGLE_CHAR_TOKENS[ch];
    if (single !== undefined) {
      scanner.advance();
      push(single, start, ch, '');
      continue;
    }

    // --- words ----------------------------------------------------------
    // A word runs until whitespace, a delimiter, or a comment opener. Anything
    // else — accents, emoji, `?`, `!`, `.`, `-` — is ordinary label material.
    let consumed = 0;
    while (!scanner.atEnd) {
      const c = scanner.peek();
      if (isWhitespace(c) || isDelimiter(c)) break;
      if (c === '/' && (scanner.peek(1) === '/' || scanner.peek(1) === '*')) break;
      scanner.advance();
      consumed += 1;
    }

    if (consumed === 0) {
      // Defensive: the loop above must always make progress. If some character
      // is simultaneously not whitespace, not a delimiter and not consumable we
      // would spin forever, so force progress and report it.
      const stray = scanner.advance();
      diagnostics.push({
        code: DiagnosticCode.UnexpectedCharacter,
        severity: 'error',
        message: `Unexpected character ${JSON.stringify(stray)}.`,
        range: { start, end: scanner.pos },
      });
      continue;
    }

    const text = scanner.slice(start.offset, scanner.pos.offset);
    push('word', start, text, text.toLowerCase());
  }

  tokens.push(makeEofToken({ start: scanner.pos, end: scanner.pos }, tokens.length));

  return { tokens, diagnostics, source };
}

function unescapeChar(ch: string): string {
  switch (ch) {
    case 'n':
      return '\n';
    case 't':
      return '\t';
    case 'r':
      return '\r';
    case '\\':
      return '\\';
    case '"':
      return '"';
    case '':
      return '';
    default:
      // Unknown escapes keep both characters, so `C:\path` in a label survives.
      return `\\${ch}`;
  }
}

/** Tokens with comments removed — the view the parser consumes. */
export function withoutComments(tokens: readonly Token[]): Token[] {
  return tokens.filter((t) => t.type !== 'line-comment' && t.type !== 'block-comment');
}
